import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, CheckCircle2, Circle, Clock, Calendar, Target, Send, Paperclip, ShieldCheck, FileText, MessageSquare, Upload, Loader2, Save, Plus, Trash2, Pencil } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import PageHeader from "../components/PageHeader";

export default function AdminAccompagnementDetail() {
  const { accId } = useParams();
  const { axiosAuth, isAdmin } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [acc, setAcc] = useState(null);
  const [notes, setNotes] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [noteTarget, setNoteTarget] = useState({ phase_index: null, task_id: null });
  const [showNoteForm, setShowNoteForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editPhases, setEditPhases] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const [uploadTarget, setUploadTarget] = useState({ phase_index: 0, task_id: "" });

  const load = async () => {
    try {
      const [accRes, notesRes, filesRes] = await Promise.all([
        api.get(`/accompaniments/${accId}`),
        api.get(`/accompaniments/${accId}/notes`),
        api.get(`/accompaniments/${accId}/files`),
      ]);
      setAcc(accRes.data);
      setNotes(notesRes.data);
      setFiles(filesRes.data);
      setEditPhases(JSON.parse(JSON.stringify(accRes.data.phases || [])));
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [accId]);

  const toggleTask = async (taskId, currentStatus) => {
    await api.put(`/accompaniments/${accId}/tasks/${taskId}`, { status: currentStatus === "done" ? "todo" : "done" });
    load();
  };

  const validateTask = async (taskId) => {
    await api.put(`/accompaniments/${accId}/tasks/${taskId}/validate`);
    load();
  };

  const addNote = async (phaseIndex, taskId) => {
    if (!noteText.trim()) return;
    await api.post(`/accompaniments/${accId}/notes`, { phase_index: phaseIndex, task_id: taskId || "", content: noteText });
    setNoteText("");
    setShowNoteForm(null);
    load();
  };

  const uploadFile = async (e, phaseIndex, taskId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    await api.post(`/accompaniments/${accId}/files?phase_index=${phaseIndex}&task_id=${taskId || ""}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const saveEditing = async () => {
    setSaving(true);
    await api.put(`/accompaniments/${accId}`, { phases: editPhases });
    setEditing(false);
    setSaving(false);
    load();
  };

  const getProgress = (phases) => {
    if (!phases?.length) return 0;
    let total = 0, done = 0;
    phases.forEach(p => p.tasks?.forEach(t => { total++; if (t.status === "done") done++; }));
    return total === 0 ? 0 : Math.round((done / total) * 100);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "";
  const isOverdue = (d) => d && new Date(d) < new Date();

  const getNotesFor = (phaseIdx, taskId) => notes.filter(n => n.phase_index === phaseIdx && (taskId ? n.task_id === taskId : !n.task_id));
  const getFilesFor = (phaseIdx, taskId) => files.filter(f => f.phase_index === phaseIdx && (taskId ? f.task_id === taskId : !f.task_id));

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;
  if (!acc) return <div className="p-8 text-red-400">Accompagnement non trouvé</div>;

  const progress = getProgress(acc.phases);
  const color = { START: "#f7c935", PLUS: "#5a2d9c", SMART: "#10b981", BLITZ: "#ef4444" }[acc.formula] || "#f7c935";

  // Editing mode
  if (editing) {
    return (
      <div className="p-8 max-w-5xl" data-testid="acc-edit-mode">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setEditing(false)} className="flex items-center gap-2 text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" /> Retour
          </button>
          <Button onClick={saveEditing} disabled={saving} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
          </Button>
        </div>
        <h2 className="text-2xl font-bold mb-6">Modifier le parcours</h2>
        <div className="space-y-6">
          {editPhases.map((phase, pi) => (
            <div key={pi} className="bg-[#14101c] border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Input value={phase.name} onChange={(e) => { const u = [...editPhases]; u[pi].name = e.target.value; setEditPhases(u); }} className="bg-[#0b0811] border-white/10 text-white font-bold focus:border-[#f7c935]" />
                <Input value={phase.period || ""} onChange={(e) => { const u = [...editPhases]; u[pi].period = e.target.value; setEditPhases(u); }} placeholder="Période" className="bg-[#0b0811] border-white/10 text-white w-32 focus:border-[#f7c935]" />
                <Input value={phase.duration || ""} onChange={(e) => { const u = [...editPhases]; u[pi].duration = e.target.value; setEditPhases(u); }} placeholder="Durée" className="bg-[#0b0811] border-white/10 text-white w-28 focus:border-[#f7c935]" />
                <Input type="date" value={phase.deadline || ""} onChange={(e) => { const u = [...editPhases]; u[pi].deadline = e.target.value; setEditPhases(u); }} className="bg-[#0b0811] border-white/10 text-white w-40 focus:border-[#f7c935]" />
                {editPhases.length > 1 && <button onClick={() => setEditPhases(editPhases.filter((_, i) => i !== pi))} className="p-1 hover:bg-red-500/10 rounded"><Trash2 className="w-4 h-4 text-red-400" /></button>}
              </div>
              <div className="space-y-2 ml-4">
                {phase.tasks?.map((task, ti) => (
                  <div key={ti} className="flex items-center gap-2">
                    <Input value={task.label} onChange={(e) => { const u = [...editPhases]; u[pi].tasks[ti].label = e.target.value; setEditPhases(u); }} className="bg-[#0b0811] border-white/10 text-white text-sm h-8 focus:border-[#f7c935]" />
                    <Input value={task.responsible || ""} onChange={(e) => { const u = [...editPhases]; u[pi].tasks[ti].responsible = e.target.value; setEditPhases(u); }} placeholder="Resp." className="bg-[#0b0811] border-white/10 text-white text-sm h-8 w-24 focus:border-[#f7c935]" />
                    <Input type="date" value={task.deadline || ""} onChange={(e) => { const u = [...editPhases]; u[pi].tasks[ti].deadline = e.target.value; setEditPhases(u); }} className="bg-[#0b0811] border-white/10 text-white text-sm h-8 w-36 focus:border-[#f7c935]" />
                    {phase.tasks.length > 1 && <button onClick={() => { const u = [...editPhases]; u[pi].tasks = u[pi].tasks.filter((_, i) => i !== ti); setEditPhases(u); }} className="p-0.5 hover:bg-red-500/10 rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>}
                  </div>
                ))}
                <button onClick={() => { const u = [...editPhases]; u[pi].tasks.push({ id: `t_${Date.now()}`, label: "", responsible: "Blitzz", deadline: "", status: "todo" }); setEditPhases(u); }} className="text-xs font-medium flex items-center gap-1 mt-1" style={{ color: "#f7c935" }}>
                  <Plus className="w-3 h-3" /> Tâche
                </button>
              </div>
            </div>
          ))}
          <Button onClick={() => setEditPhases([...editPhases, { name: `Phase ${editPhases.length + 1}`, period: "", duration: "", deadline: "", tasks: [{ id: `t_${Date.now()}`, label: "", responsible: "Blitzz", deadline: "", status: "todo" }] }])} variant="outline" className="border-white/10 text-white hover:bg-white/5">
            <Plus className="w-4 h-4 mr-2" /> Ajouter une phase
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl" data-testid="acc-detail-page">
      <PageHeader
        icon={<Target />}
        title={acc.title}
        description={acc.description || `Accompagnement ${acc.formula} · Avancement ${progress}%`}
        breadcrumb={[{ label: "Admin" }, { label: "Accompagnements" }, { label: acc.title }]}
        testId="acc-detail-header"
        actions={(
          <>
            <Button onClick={() => navigate(-1)} variant="ghost" className="text-slate-400 hover:bg-white/5" data-testid="acc-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <span className="text-xs px-2.5 py-1 rounded font-bold" style={{ background: `${color}20`, color }}>{acc.formula}</span>
            <span className="text-2xl font-black ml-2" style={{ color }}>{progress}%</span>
            {isAdmin && (
              <>
                <Button onClick={() => setEditing(true)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="edit-acc-btn">
                  <Pencil className="w-4 h-4 mr-2" /> Modifier
                </Button>
                <Button
                  onClick={async () => {
                    if (!window.confirm(`Supprimer definitivement l'accompagnement "${acc.title}" ?`)) return;
                    try {
                      await api.delete(`/accompaniments/${accId}`);
                      navigate("/admin/accompagnements");
                    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
                  }}
                  className="bg-red-500/15 text-red-400 hover:bg-red-500/25 font-bold"
                  data-testid="delete-acc-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                </Button>
              </>
            )}
          </>
        )}
      />

      {/* Dates */}
      <div className="flex items-center gap-6 text-sm text-slate-500 mb-4">
        {acc.start_date && <div className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Début : {formatDate(acc.start_date)}</div>}
        {acc.end_date && <div className="flex items-center gap-1.5"><Target className="w-4 h-4" /> Fin : {formatDate(acc.end_date)}</div>}
      </div>
      <Progress value={progress} className="h-2.5 bg-[#0b0811] mb-8" />

      {/* Timeline */}
      <div className="relative ml-4">
        <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: `${color}30` }} />
        {acc.phases?.map((phase, pi) => {
          const phaseDone = phase.tasks?.every(t => t.status === "done");
          const phaseOverdue = phase.deadline && isOverdue(phase.deadline) && !phaseDone;
          const phaseNotes = getNotesFor(pi, null);
          const phaseFiles = getFilesFor(pi, null);

          return (
            <div key={pi} className="relative pl-8 pb-10 last:pb-0">
              <div className={`absolute left-0 -translate-x-1/2 w-4 h-4 rounded-full border-2 ${phaseDone ? "border-green-400 bg-green-400" : phaseOverdue ? "border-red-400 bg-red-400/20" : "bg-[#0b0811]"}`}
                style={{ borderColor: phaseDone ? undefined : phaseOverdue ? undefined : color }} />

              <div className="flex items-center gap-3 flex-wrap mb-3">
                <h3 className="text-base font-bold">{phase.name}</h3>
                {phase.period && <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {phase.period}</span>}
                {phase.duration && <span className="text-xs text-slate-500">({phase.duration})</span>}
                {phase.deadline && <span className={`text-xs flex items-center gap-1 ${phaseOverdue ? "text-red-400" : "text-slate-500"}`}><Calendar className="w-3 h-3" /> {formatDate(phase.deadline)}</span>}
                {/* Phase actions */}
                <div className="flex items-center gap-1 ml-auto">
                  <button onClick={() => { setShowNoteForm(`phase-${pi}`); setNoteTarget({ phase_index: pi, task_id: null }); }} className="p-1.5 rounded hover:bg-white/5" title="Ajouter une note">
                    <MessageSquare className="w-4 h-4 text-slate-500" />
                  </button>
                  <input ref={fileRef} type="file" className="hidden" onChange={(e) => uploadFile(e, pi, "")} />
                  <button onClick={() => { setUploadTarget({ phase_index: pi, task_id: "" }); fileRef.current?.click(); }} className="p-1.5 rounded hover:bg-white/5" title="Joindre un fichier">
                    <Paperclip className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              </div>

              {/* Phase-level notes */}
              {phaseNotes.length > 0 && (
                <div className="mb-3 space-y-1.5">
                  {phaseNotes.map(n => (
                    <div key={n.note_id} className="flex items-start gap-2 text-xs p-2 rounded bg-[#0b0811] border border-white/5">
                      <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: n.author_role === "admin" ? "#f7c935" : "#5a2d9c" }} />
                      <div><span className="font-semibold" style={{ color: n.author_role === "admin" ? "#f7c935" : "#5a2d9c" }}>{n.author_name}</span>: {n.content}<span className="text-slate-600 ml-2">{formatDate(n.created_at)}</span></div>
                    </div>
                  ))}
                </div>
              )}
              {phaseFiles.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {phaseFiles.map(f => (
                    <span key={f.file_id} className="text-xs bg-[#0b0811] border border-white/5 rounded px-2 py-1 flex items-center gap-1"><FileText className="w-3 h-3" />{f.filename}</span>
                  ))}
                </div>
              )}

              {/* Note form for phase */}
              {showNoteForm === `phase-${pi}` && (
                <div className="mb-3 flex items-center gap-2">
                  <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Écrire une note..." className="bg-[#0b0811] border-white/10 text-white text-sm h-9 flex-1 focus:border-[#f7c935]" />
                  <Button size="sm" onClick={() => addNote(pi, null)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] h-9"><Send className="w-3.5 h-3.5" /></Button>
                </div>
              )}

              {/* Tasks */}
              <div className="space-y-2">
                {phase.tasks?.map(task => {
                  const taskOverdue = task.deadline && isOverdue(task.deadline) && task.status !== "done";
                  const taskNotes = getNotesFor(pi, task.id);
                  const taskFiles = getFilesFor(pi, task.id);
                  return (
                    <div key={task.id} className={`rounded-lg border transition-colors ${task.status === "done" ? "bg-green-500/5 border-green-500/20" : taskOverdue ? "bg-red-500/5 border-red-500/20" : "bg-[#0b0811] border-white/5"}`}>
                      <div className="flex items-center gap-3 p-3">
                        <button onClick={() => toggleTask(task.id, task.status)}>
                          {task.status === "done" ? <CheckCircle2 className="w-5 h-5 text-green-400" /> : <Circle className="w-5 h-5 text-slate-600" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <span className={`text-sm ${task.status === "done" ? "line-through text-slate-500" : ""}`}>{task.label}</span>
                          {task.deadline && <span className={`text-xs ml-2 ${taskOverdue ? "text-red-400" : "text-slate-600"}`}>({formatDate(task.deadline)})</span>}
                        </div>
                        {task.responsible && <span className="text-xs text-slate-600">{task.responsible}</span>}
                        {/* Validated badge */}
                        {task.validated_by_admin && <ShieldCheck className="w-4 h-4 text-green-400 flex-shrink-0" title="Validé par admin" />}
                        {/* Admin validate button */}
                        {isAdmin && task.status === "done" && !task.validated_by_admin && (
                          <button onClick={() => validateTask(task.id)} className="text-xs px-2 py-1 rounded bg-green-500/10 text-green-400 hover:bg-green-500/20 font-medium" data-testid={`validate-${task.id}`}>
                            Valider
                          </button>
                        )}
                        <button onClick={() => { setShowNoteForm(`task-${task.id}`); setNoteTarget({ phase_index: pi, task_id: task.id }); }} className="p-1 rounded hover:bg-white/5"><MessageSquare className="w-3.5 h-3.5 text-slate-500" /></button>
                        <input type="file" className="hidden" id={`file-${task.id}`} onChange={(e) => uploadFile(e, pi, task.id)} />
                        <button onClick={() => document.getElementById(`file-${task.id}`)?.click()} className="p-1 rounded hover:bg-white/5"><Paperclip className="w-3.5 h-3.5 text-slate-500" /></button>
                      </div>
                      {/* Task notes & files */}
                      {(taskNotes.length > 0 || taskFiles.length > 0 || showNoteForm === `task-${task.id}`) && (
                        <div className="px-3 pb-3 pt-0 border-t border-white/5">
                          {taskNotes.map(n => (
                            <div key={n.note_id} className="flex items-start gap-2 text-xs py-1">
                              <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: n.author_role === "admin" ? "#f7c935" : "#5a2d9c" }} />
                              <div><span className="font-semibold" style={{ color: n.author_role === "admin" ? "#f7c935" : "#5a2d9c" }}>{n.author_name}</span>: {n.content}</div>
                            </div>
                          ))}
                          {taskFiles.map(f => (
                            <span key={f.file_id} className="text-xs bg-[#14101c] border border-white/5 rounded px-2 py-0.5 inline-flex items-center gap-1 mr-1 mt-1"><FileText className="w-3 h-3" />{f.filename}</span>
                          ))}
                          {showNoteForm === `task-${task.id}` && (
                            <div className="flex items-center gap-2 mt-2">
                              <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Répondre..." className="bg-[#14101c] border-white/10 text-white text-xs h-8 flex-1 focus:border-[#f7c935]" />
                              <Button size="sm" onClick={() => addNote(pi, task.id)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] h-8 text-xs"><Send className="w-3 h-3" /></Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <LinkedAuditsSection accId={accId} clientId={acc.client_id} api={api} />
    </div>
  );
}

// ─── Linked Audits Section ─────────────────────────────────
function LinkedAuditsSection({ accId, clientId, api }) {
  const [audits, setAudits] = useState([]);
  const [allAudits, setAllAudits] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAttach, setShowAttach] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedAuditId, setSelectedAuditId] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [linked, all, tpls] = await Promise.all([
        api.get(`/accompaniments/${accId}/audits`),
        api.get(`/audits/client/${clientId}`),
        api.get("/audit-templates"),
      ]);
      setAudits(linked.data || []);
      setAllAudits(all.data || []);
      setTemplates(tpls.data || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accId]);

  const attach = async () => {
    if (!selectedAuditId) return;
    await api.post(`/audits/${selectedAuditId}/attach`, { accompaniment_id: accId });
    setShowAttach(false); setSelectedAuditId(""); load();
  };

  const detach = async (auditId) => {
    if (!window.confirm("Detacher cet audit ?")) return;
    await api.post(`/audits/${auditId}/detach`);
    load();
  };

  const createFromTemplate = async () => {
    if (!selectedTemplateId) return;
    await api.post("/audits/from-template", {
      client_id: clientId,
      template_id: selectedTemplateId,
      accompaniment_id: accId,
      title: newTitle || null,
    });
    setShowCreate(false); setSelectedTemplateId(""); setNewTitle(""); load();
  };

  const attachables = allAudits.filter(a => !a.accompaniment_id);

  return (
    <div className="mt-6 p-5 rounded-2xl bg-[#14101c] border border-white/5" data-testid="linked-audits-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#f7c935]" /> Audits lies ({audits.length})</h3>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowAttach(true)} disabled={attachables.length === 0} className="bg-white/5 hover:bg-white/10 text-xs" data-testid="attach-audit-btn"><Plus className="w-3 h-3 mr-1" />Rattacher</Button>
          <Button size="sm" onClick={() => setShowCreate(true)} disabled={templates.length === 0} className="bg-[#f7c935] text-[#14101c] text-xs hover:bg-[#ffd948]" data-testid="create-audit-from-tpl-btn"><Plus className="w-3 h-3 mr-1" />Creer depuis modele</Button>
        </div>
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-[#f7c935]" /> :
        audits.length === 0 ? <div className="text-sm text-slate-500">Aucun audit rattache a cet accompagnement.</div> :
        <div className="space-y-2">
          {audits.map(a => (
            <div key={a.audit_id} className="bg-[#0b0811] border border-white/5 rounded p-3 flex items-center justify-between" data-testid={`linked-audit-${a.audit_id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#f7c935]" />
                  <span className="font-semibold">{a.title}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-white/5">{a.status}</span>
                  {a.mode && <span className="text-[10px] px-2 py-0.5 rounded bg-white/5">{a.mode}</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{a.sections?.length || 0} sections</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/audits`)} className="text-[#f7c935] text-xs">Ouvrir</Button>
                <Button size="sm" variant="ghost" onClick={() => detach(a.audit_id)} className="text-red-400 text-xs" data-testid={`detach-${a.audit_id}`}>Detacher</Button>
              </div>
            </div>
          ))}
        </div>
      }
      {showAttach && (
        <div className="mt-3 bg-[#0b0811] border border-white/10 rounded p-3 space-y-2" data-testid="attach-dialog">
          <div className="text-xs text-slate-400">Selectionnez un audit deja cree pour ce client :</div>
          <select value={selectedAuditId} onChange={e => setSelectedAuditId(e.target.value)} className="bg-[#0b0811] border border-white/10 text-white px-3 py-2 rounded w-full text-sm">
            <option value="">— Selectionner —</option>
            {attachables.map(a => <option key={a.audit_id} value={a.audit_id}>{a.title}</option>)}
          </select>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setShowAttach(false)} className="text-xs">Annuler</Button>
            <Button size="sm" onClick={attach} className="bg-[#f7c935] text-[#14101c] text-xs" data-testid="confirm-attach-btn">Rattacher</Button>
          </div>
        </div>
      )}
      {showCreate && (
        <div className="mt-3 bg-[#0b0811] border border-white/10 rounded p-3 space-y-2" data-testid="create-tpl-dialog">
          <div className="text-xs text-slate-400">Creez un nouvel audit depuis un modele :</div>
          <select value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)} className="bg-[#0b0811] border border-white/10 text-white px-3 py-2 rounded w-full text-sm">
            <option value="">— Modele —</option>
            {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
          </select>
          <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Titre (optionnel)" className="bg-[#0b0811] border-white/10 text-white text-sm" />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)} className="text-xs">Annuler</Button>
            <Button size="sm" onClick={createFromTemplate} className="bg-[#f7c935] text-[#14101c] text-xs" data-testid="confirm-create-btn">Creer</Button>
          </div>
        </div>
      )}
    </div>
  );
}
